import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { db } from '../../../firebase';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';

const Payouts = ({ userId, totalRevenue }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isSavingBank, setIsSavingBank] = useState(false);
    const [isRequesting, setIsRequesting] = useState(false);
    const [showBankForm, setShowBankForm] = useState(false);
    
    // Bank & UPI Details state
    const [bankDetails, setBankDetails] = useState({
        bankName: '',
        holderName: '',
        accountNumber: '',
        ifscCode: '',
        upiId: ''
    });

    // Payout requests history
    const [requests, setRequests] = useState([]);
    const [dbWithdrawnAmount, setDbWithdrawnAmount] = useState(0);

    // Selected request for the digital slip modal
    const [selectedReceipt, setSelectedReceipt] = useState(null);

    // Fetch details on mount or userId change
    const fetchPayoutDetails = async () => {
        if (!userId) return;
        try {
            setIsLoading(true);
            const docRef = doc(db, 'franchiseAdmins', userId);
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                const data = snap.data();
                setBankDetails({
                    bankName: data.bankName || (data.bankDetails?.bankName || ''),
                    holderName: data.accountHolderName || (data.bankDetails?.holderName || ''),
                    accountNumber: data.accountNumber || (data.bankDetails?.accountNumber || ''),
                    ifscCode: data.ifscCode || (data.bankDetails?.ifscCode || ''),
                    upiId: data.upiId || (data.bankDetails?.upiId || '')
                });
                setRequests(data.withdrawalRequests || []);
                setDbWithdrawnAmount(data.withdrawnAmount || 0);
            }
        } catch (error) {
            console.error("Error fetching payout data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPayoutDetails();
    }, [userId]);

    // Calculate metrics
    const completedRequests = requests.filter(r => r.status === 'Completed' || r.status === 'Paid');
    const pendingRequests = requests.filter(r => r.status === 'Pending');

    const totalPaidOut = completedRequests.reduce((sum, r) => sum + r.amount, 0);
    const totalPending = pendingRequests.reduce((sum, r) => sum + r.amount, 0);
    
    // Balance calculation: Total minus what has been paid out or is currently pending
    const availableBalance = Math.max(0, totalRevenue - totalPaidOut - totalPending);

    // Bank Details validation check
    const isBankConfigured = (bankDetails.bankName && bankDetails.holderName && bankDetails.accountNumber && bankDetails.ifscCode) || bankDetails.upiId;

    const handleBankDetailsSubmit = async (e) => {
        e.preventDefault();
        if (!userId) return;

        try {
            setIsSavingBank(true);
            const docRef = doc(db, 'franchiseAdmins', userId);
            await updateDoc(docRef, {
                bankName: bankDetails.bankName,
                accountHolderName: bankDetails.holderName,
                accountNumber: bankDetails.accountNumber,
                ifscCode: bankDetails.ifscCode,
                upiId: bankDetails.upiId,
                bankDetails: bankDetails // Nested object fallback
            });

            Swal.fire({
                title: 'Details Saved!',
                text: 'Your bank and UPI configurations have been updated successfully.',
                icon: 'success',
                confirmButtonColor: '#1a7a5e'
            });
            setShowBankForm(false);
        } catch (error) {
            console.error("Error saving bank details:", error);
            Swal.fire('Error', 'Failed to save details. Please try again.', 'error');
        } finally {
            setIsSavingBank(false);
        }
    };

    const requestPayout = async () => {
        if (!isBankConfigured) {
            Swal.fire({
                title: 'Payment Details Missing',
                text: 'Please set up your Bank Account or UPI details first so we know where to send your funds.',
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Configure Now',
                confirmButtonColor: '#1a7a5e',
                cancelButtonText: 'Cancel'
            }).then((result) => {
                if (result.isConfirmed) {
                    setShowBankForm(true);
                }
            });
            return;
        }

        if (availableBalance < 1000) {
            Swal.fire({
                title: 'Minimum Limit Required',
                text: 'You need at least ₹1,000 in available balance to request a payout.',
                icon: 'warning',
                confirmButtonColor: '#1a7a5e'
            });
            return;
        }

        const confirmText = bankDetails.upiId 
            ? `Confirm payout request of ₹${availableBalance.toLocaleString('en-IN')} to UPI: ${bankDetails.upiId}?`
            : `Confirm payout request of ₹${availableBalance.toLocaleString('en-IN')} to Bank A/C ending in ...${bankDetails.accountNumber.slice(-4)}?`;

        Swal.fire({
            title: 'Request Payout?',
            text: confirmText,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Submit Request',
            confirmButtonColor: '#1a7a5e',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                setIsRequesting(true);
                try {
                    const adminRef = doc(db, 'franchiseAdmins', userId);
                    const request = {
                        id: `REQ${Date.now()}`,
                        amount: availableBalance,
                        status: 'Pending',
                        date: new Date().toISOString(),
                        accountSnapshot: bankDetails.upiId 
                            ? `UPI ID: ${bankDetails.upiId}` 
                            : `${bankDetails.bankName} (A/C: ...${bankDetails.accountNumber.slice(-4)})`
                    };

                    await updateDoc(adminRef, {
                        withdrawalRequests: arrayUnion(request)
                    });

                    Swal.fire({
                        title: 'Request Sent!',
                        text: `Your payout request for ₹${availableBalance.toLocaleString('en-IN')} has been submitted and is under review.`,
                        icon: 'success',
                        confirmButtonColor: '#1a7a5e'
                    });

                    // Reload local layout
                    fetchPayoutDetails();
                } catch (error) {
                    console.error("Error submitting payout:", error);
                    Swal.fire('Error', 'Failed to request payout. Please try again.', 'error');
                } finally {
                    setIsRequesting(false);
                }
            }
        });
    };

    if (isLoading) {
        return (
            <div style={{ background: 'white', padding: '3rem', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', textAlign: 'center', color: '#64748b' }}>
                <div style={{ display: 'inline-block', width: '40px', height: '40px', border: '3px solid #f3f3f3', borderTop: '3px solid #1a7a5e', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '1rem' }} />
                <div>Loading Payout Records...</div>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            
            {/* Balance cards overview */}
            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                    <h3 style={{ margin: 0, color: '#1e293b' }}>Earnings & Payouts</h3>
                    <button
                        onClick={() => setShowBankForm(!showBankForm)}
                        style={{
                            background: '#f1f5f9',
                            border: '1px solid #cbd5e1',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            fontWeight: 600,
                            color: '#475569',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '0.85rem'
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                        {isBankConfigured ? 'Edit Bank/UPI Info' : 'Setup Payout Method'}
                    </button>
                </div>

                {/* Bank / UPI settings drawer */}
                {showBankForm && (
                    <form onSubmit={handleBankDetailsSubmit} style={{ background: '#f8fafc', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', '@media (maxWidth: 600px)': { gridTemplateColumns: '1fr' } }}>
                        <h4 style={{ margin: 0, gridColumn: '1 / -1', color: '#1e293b', fontSize: '1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>🏦 Configure Bank Transfer or UPI details</h4>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569' }}>Account Holder Name</label>
                            <input
                                type="text"
                                value={bankDetails.holderName}
                                onChange={(e) => setBankDetails({ ...bankDetails, holderName: e.target.value })}
                                placeholder="Name as in Bank Account"
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                            />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569' }}>Bank Name</label>
                            <input
                                type="text"
                                value={bankDetails.bankName}
                                onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                                placeholder="e.g. State Bank of India"
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                            />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569' }}>Account Number</label>
                            <input
                                type="text"
                                value={bankDetails.accountNumber}
                                onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
                                placeholder="Your 9-18 digit account number"
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                            />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569' }}>IFSC Code</label>
                            <input
                                type="text"
                                value={bankDetails.ifscCode}
                                onChange={(e) => setBankDetails({ ...bankDetails, ifscCode: e.target.value })}
                                placeholder="e.g. SBIN0001234"
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                            />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', gridColumn: '1 / -1' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '8px 0' }}>
                                <div style={{ flex: 1, height: '1px', background: '#cbd5e1' }} />
                                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>OR USE UPI</span>
                                <div style={{ flex: 1, height: '1px', background: '#cbd5e1' }} />
                            </div>
                            <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569' }}>UPI ID (GPay / PhonePe / Paytm)</label>
                            <input
                                type="text"
                                value={bankDetails.upiId}
                                onChange={(e) => setBankDetails({ ...bankDetails, upiId: e.target.value })}
                                placeholder="username@upi"
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '10px', gridColumn: '1 / -1', marginTop: '10px' }}>
                            <button
                                type="submit"
                                disabled={isSavingBank}
                                style={{ background: '#1a7a5e', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' }}
                            >
                                {isSavingBank ? 'Saving...' : 'Save Settings'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowBankForm(false)}
                                style={{ background: 'white', border: '1px solid #cbd5e1', padding: '10px 20px', borderRadius: '6px', fontWeight: 600, color: '#475569', cursor: 'pointer' }}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                )}
                
                {/* 4 Cards Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>Total Revenue</div>
                        <div style={{ fontSize: '1.75rem', color: '#0f172a', fontWeight: 800 }}>₹{totalRevenue.toLocaleString('en-IN')}</div>
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>Lifetime gross earnings</span>
                    </div>
                    
                    <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>Lifetime Paid</div>
                        <div style={{ fontSize: '1.75rem', color: '#16a34a', fontWeight: 800 }}>₹{totalPaidOut.toLocaleString('en-IN')}</div>
                        <span style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>Cleared to bank</span>
                    </div>

                    <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>In Processing</div>
                        <div style={{ fontSize: '1.75rem', color: '#ea580c', fontWeight: 800 }}>₹{totalPending.toLocaleString('en-IN')}</div>
                        <span style={{ fontSize: '0.75rem', color: '#ea580c', fontWeight: 600 }}>Pending admin clearance</span>
                    </div>
                    
                    <div style={{ background: 'linear-gradient(135deg, #10b981 0%, #15803d 100%)', padding: '1.25rem', borderRadius: '12px', color: 'white', display: 'flex', flexDirection: 'column', justifyBetween: 'space-between', boxShadow: '0 4px 12px rgba(21,128,61,0.15)' }}>
                        <div>
                            <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.85)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.3rem' }}>Available Balance</div>
                            <div style={{ fontSize: '1.85rem', fontWeight: 800 }}>₹{availableBalance.toLocaleString('en-IN')}</div>
                        </div>
                        
                        <button 
                            onClick={requestPayout}
                            disabled={isRequesting || availableBalance < 1000}
                            style={{ 
                                marginTop: '1rem', 
                                width: '100%', 
                                background: 'white', 
                                color: '#15803d', 
                                border: 'none', 
                                padding: '8px 12px', 
                                borderRadius: '8px', 
                                fontWeight: 700, 
                                cursor: (isRequesting || availableBalance < 1000) ? 'not-allowed' : 'pointer',
                                opacity: (isRequesting || availableBalance < 1000) ? 0.6 : 1,
                                fontSize: '0.85rem',
                                transition: 'all 0.2s',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                            }}
                        >
                            {isRequesting ? 'Processing...' : availableBalance < 1000 ? '₹1,000 Min Req' : 'Withdraw Funds'}
                        </button>
                    </div>
                </div>
                
                <p style={{ fontSize: '0.8rem', color: '#64748b', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                    Withdrawal minimum amount is ₹1,000. Balance locks upon withdrawal request. Processing completes in 3-5 bank business days.
                </p>
            </div>

            {/* Payout requests transaction history */}
            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
                <h3 style={{ margin: '0 0 1rem 0', color: '#1e293b' }}>Withdrawal Request History</h3>
                
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #cbd5e1' }}>
                                <th style={{ padding: '10px 15px', textAlign: 'left', color: '#475569', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase' }}>Request ID</th>
                                <th style={{ padding: '10px 15px', textAlign: 'left', color: '#475569', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase' }}>Amount</th>
                                <th style={{ padding: '10px 15px', textAlign: 'left', color: '#475569', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase' }}>Submitted Date</th>
                                <th style={{ padding: '10px 15px', textAlign: 'left', color: '#475569', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase' }}>Account / Transfer Target</th>
                                <th style={{ padding: '10px 15px', textAlign: 'left', color: '#475569', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase' }}>Status</th>
                                <th style={{ padding: '10px 15px', textAlign: 'center', color: '#475569', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.9rem' }}>
                                        No payout requests requested yet. Earn commissions and request payouts!
                                    </td>
                                </tr>
                            ) : (
                                [...requests].sort((a,b) => new Date(b.date) - new Date(a.date)).map((req) => (
                                    <tr key={req.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '12px 15px', fontWeight: 700, color: '#334155', fontSize: '0.85rem' }}>{req.id}</td>
                                        <td style={{ padding: '12px 15px', fontWeight: 800, color: '#0f172a', fontSize: '0.9rem' }}>₹{req.amount.toLocaleString('en-IN')}</td>
                                        <td style={{ padding: '12px 15px', color: '#475569', fontSize: '0.85rem' }}>
                                            {new Date(req.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </td>
                                        <td style={{ padding: '12px 15px', color: '#64748b', fontSize: '0.85rem', fontWeight: 500 }}>
                                            {req.accountSnapshot || 'Configured Account'}
                                        </td>
                                        <td style={{ padding: '12px 15px' }}>
                                            <span style={{
                                                padding: '4px 8px',
                                                borderRadius: '6px',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                background: (req.status === 'Completed' || req.status === 'Paid') ? '#dcfce7' : req.status === 'Rejected' ? '#fee2e2' : '#fef9c3',
                                                color: (req.status === 'Completed' || req.status === 'Paid') ? '#166534' : req.status === 'Rejected' ? '#991b1b' : '#854d0e'
                                            }}>
                                                {req.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px 15px', textAlign: 'center' }}>
                                            {(req.status === 'Completed' || req.status === 'Paid') ? (
                                                <button
                                                    onClick={() => setSelectedReceipt(req)}
                                                    style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem' }}
                                                >
                                                    View Slip 🧾
                                                </button>
                                            ) : req.status === 'Rejected' ? (
                                                <button
                                                    onClick={() => Swal.fire({ title: 'Rejection Reason', text: req.rejectionReason || 'No reason specified by administrator.', icon: 'info', confirmButtonColor: '#1a7a5e' })}
                                                    style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem' }}
                                                >
                                                    View Info ℹ️
                                                </button>
                                            ) : (
                                                <span style={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 500 }}>In Review</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Digital Payout Slip Modal */}
            {selectedReceipt && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
                    <div style={{ background: 'white', width: '100%', maxWidth: '420px', borderRadius: '16px', boxShadow: '0 20px 25px rgba(0,0,0,0.15)', overflow: 'hidden', border: '1px solid #cbd5e1' }}>
                        <div style={{ background: 'linear-gradient(135deg, #10b981 0%, #15803d 100%)', padding: '1.5rem', color: 'white', textAlign: 'center', position: 'relative' }}>
                            <button
                                onClick={() => setSelectedReceipt(null)}
                                style={{ position: 'absolute', top: '15px', right: '15px', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1.25rem' }}
                            >
                                &times;
                            </button>
                            <div style={{ fontSize: '1.5rem', fontWeight: 800, letterSpacing: '0.5px' }}>Yathartha</div>
                            <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>Payment Receipt Slip</span>
                        </div>

                        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem', fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #e2e8f0', paddingBottom: '8px' }}>
                                <strong>RECEIPT ID:</strong>
                                <span>{selectedReceipt.id}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #e2e8f0', paddingBottom: '8px' }}>
                                <strong>STATUS:</strong>
                                <span style={{ color: '#16a34a', fontWeight: 'bold' }}>SUCCESSFUL</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #e2e8f0', paddingBottom: '8px' }}>
                                <strong>SETTLED ON:</strong>
                                <span>{new Date(selectedReceipt.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #e2e8f0', paddingBottom: '8px' }}>
                                <strong>TRANSFER TARGET:</strong>
                                <span style={{ textAlign: 'right' }}>{selectedReceipt.accountSnapshot}</span>
                            </div>
                            
                            <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center', margin: '8px 0' }}>
                                <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>TRANSFER AMOUNT</span>
                                <span style={{ fontSize: '1.75rem', fontWeight: '800', color: '#0f172a' }}>₹{selectedReceipt.amount.toLocaleString('en-IN')}</span>
                            </div>

                            <button
                                onClick={() => window.print()}
                                style={{ width: '100%', background: '#1e293b', color: 'white', border: 'none', padding: '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', marginTop: '10px' }}
                            >
                                Print Slip 🖨️
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Payouts;
